import React, { useState, useRef } from 'react';
import placeholderImage from '../../images/placeholder.png';
import './ImageWithFallback.css';

interface ImageWithFallbackProps {
    src: string | undefined;
    alt: string;
    className?: string;
    fallbackSrc?: string;
    onError?: () => void;
    onLoad?: () => void;
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'high' | 'low' | 'auto';
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
    src,
    alt,
    className = '',
    fallbackSrc = placeholderImage,
    onError,
    onLoad,
    loading = 'eager',
    fetchPriority = 'auto'
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const hasCalledOnLoadRef = useRef(false);
    const imgRef = useRef<HTMLImageElement>(null);
    
    // Check if src is valid (not undefined, null, or empty string)
    const hasValidSrc = src && src.trim() !== '';
    
    // If no valid src, use fallback
    const imageSrc = hasValidSrc ? src : fallbackSrc;

    const handleError = () => {
        // On error, try to set the fallback src directly on the img element
        if (imgRef.current && imgRef.current.src !== fallbackSrc) {
            imgRef.current.src = fallbackSrc;
        }
        setImageLoaded(true); // Hide skeleton even on error
        onError?.();
        if (!hasCalledOnLoadRef.current && onLoad) {
            hasCalledOnLoadRef.current = true;
            onLoad();
        }
    };

    const handleLoad = () => {
        setImageLoaded(true);
        if (!hasCalledOnLoadRef.current && onLoad) {
            hasCalledOnLoadRef.current = true;
            onLoad();
        }
    };

    return (
        <div className={`image-with-fallback ${className}`}>
            {/* Show placeholder background until image loads */}
            {!imageLoaded && (
                <div 
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f0f0f0',
                        zIndex: 0
                    }}
                />
            )}
            <img
                ref={imgRef}
                src={imageSrc}
                alt={alt}
                onError={handleError}
                onLoad={handleLoad}
                loading={loading}
                // @ts-ignore - fetchPriority is supported in modern browsers
                fetchpriority={fetchPriority}
                decoding="async"
            />
        </div>
    );
};

export default ImageWithFallback;
